import { Download, Check } from "lucide-react";
import { Reveal, Tag, Heading } from "../Reveal";

const CORE = [
  "Full interior & exterior gut rehabilitation",
  "New construction (residential & multifamily)",
  "Framing, drywall, insulation, flooring & tile",
  "Lead abatement & environmental remediation",
  "Commercial & institutional construction",
  "Design-build & project management",
];

const NAICS = [
  { group: "Primary", codes: [["236220", "Commercial & Institutional Building Construction"], ["236118", "Residential Remodelers"]] },
  { group: "New construction", codes: [["236115", "New Single-Family Housing Construction"], ["236116", "New Multifamily Housing Construction"]] },
  { group: "Trades & specialty", codes: [["238310", "Drywall & Insulation"], ["238130", "Framing Contractors"], ["238340", "Tile & Terrazzo"], ["238330", "Flooring Contractors"], ["238110", "Concrete / Foundation"], ["238160", "Roofing Contractors"]] },
];

const CERTS = ["DBE Certified", "Lead Abatement Firm Certified"];

const PAST = [
  { client: "City of Wilmington", scope: "30+ gut rehabs", text: "Completed over 30 interior gut rehabs across municipal buildings, offices, and community spaces." },
  { client: "NCRC Development Corporation", scope: "23 gut rehabs", text: "Delivered 23 residential and community-based gut rehab projects focused on revitalization, code compliance, and improved housing conditions." },
  { client: "Salvation Army", scope: "9 gut rehabs", text: "Completed 9 interior gut rehabilitation projects upgrading and modernizing facilities for continued community service." },
  { client: "Center Baptist Church", scope: "Multiple gut rehabs", text: "Enhanced facility functionality while maintaining uninterrupted operations." },
  { client: "Bancroft Construction", scope: "Bank-to-school conversion", text: "Converted an existing bank into a fully functional school including demolition, structural upgrades, MEP systems, and ADA compliance." },
  { client: "Wilmington DE-LEAD Program", scope: "41 projects", text: "Completed 41 lead abatement projects ensuring safe residential environments." },
  { client: "CH&E", scope: "HUD senior living · 26 units", text: "Delivered framing, insulation, drywall, painting, and exterior systems (Tyvek/DensGlass) in compliance with HUD standards." },
];

const Sub = ({ children }) => <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-ink/50">{children}</p>;

export const Capabilities = () => (
  <section id="capabilities" className="py-24 lg:py-40 border-t border-ink/10" data-testid="capabilities-section">
    <div className="mx-auto max-w-[1600px] px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-10 items-end">
        <Reveal className="lg:col-span-7">
          <Tag>Capabilities statement</Tag>
          <Heading className="mt-6">Built for municipal, HUD and commercial work.</Heading>
        </Reveal>
        <Reveal className="lg:col-span-5 lg:justify-self-end" delay={0.1}>
          <a
            href="/images/home/capabilities-statement.webp"
            download="Construction-Alliance-Capabilities-Statement.webp"
            target="_blank"
            rel="noreferrer"
            data-testid="capabilities-download-link"
            className="group inline-flex items-center gap-4 bg-ink text-bone font-display font-medium text-sm sm:text-base px-6 py-4 hover:bg-teal transition-colors duration-300"
          >
            Download the capabilities statement
            <Download className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
          </a>
        </Reveal>
      </div>

      <div className="mt-20 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-4 space-y-12">
          <Reveal data-testid="capabilities-core">
            <Sub>Core capabilities</Sub>
            <ul className="mt-4 divide-y divide-ink/10 border-y border-ink/10">
              {CORE.map((c) => (
                <li key={c} className="flex items-start gap-3 py-3 font-display font-medium text-lg text-ink leading-snug">
                  <Check className="h-4 w-4 mt-1.5 text-teal shrink-0" strokeWidth={3} />{c}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.05}>
            <Sub>Certifications</Sub>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="capabilities-certs">
              {CERTS.map((c) => <span key={c} className="bg-teal text-bone font-sans font-semibold text-sm px-4 py-2">{c}</span>)}
            </div>
          </Reveal>
          <Reveal delay={0.1} data-testid="capabilities-naics">
            <Sub>NAICS codes</Sub>
            <div className="mt-4 space-y-5">
              {NAICS.map((g) => (
                <div key={g.group}>
                  <p className="font-display font-medium text-base text-ink">{g.group}</p>
                  <ul className="mt-2 space-y-1.5">
                    {g.codes.map(([code, label]) => (
                      <li key={code} className="flex gap-3 text-sm text-ink/65"><span className="font-mono text-ink shrink-0">{code}</span>{label}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7 lg:col-start-6">
          <Reveal><Sub>Past performance</Sub></Reveal>
          <div className="mt-4 border-t border-ink/15" data-testid="capabilities-past-performance">
            {PAST.map((p, i) => (
              <Reveal key={p.client} delay={i * 0.04} y={20}>
                <div className="group grid sm:grid-cols-12 gap-3 sm:gap-6 py-6 border-b border-ink/15">
                  <div className="sm:col-span-5">
                    <h3 className="font-display font-medium text-xl sm:text-2xl tracking-[-0.015em] text-ink group-hover:text-teal transition-colors duration-300">{p.client}</h3>
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.14em] text-ink/50">{p.scope}</p>
                  </div>
                  <p className="sm:col-span-7 text-sm sm:text-base text-ink/65 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);
